import type { Violation } from '@finn-no/mobility-item-insertion-shared';
import pulse from '@finn-no/pulse-sdk';
import type { FieldErrors } from 'react-hook-form';

interface UseErrorTrackingProps {
  category: string;
}

interface ErrorInfo {
  field: string;
  type: string;
  message?: string;
}

/**
 * Hook to consolidate all error tracking logic.
 * Tracks both validation errors (TAI_ev01) and violation errors (TAI_ev02) using Pulse.
 */
export const useErrorTracking = ({ category }: UseErrorTrackingProps) => {
  /**
   * Track validation errors that occur during form submission.
   * These are client-side or server-side validation errors.
   * Event ID: TAI_ev01
   */
  const trackValidationErrors = (errors: ErrorInfo[]) => {
    for (const error of errors) {
      pulse.trackEvent({
        type: 'Click',
        intent: 'Error',
        name: 'Validation error',
        object: {
          id: 'TAI_ev01',
          type: 'UIElement',
          elementType: 'Button',
          name: `Field: ${error.field}, type: (${error.type}), message: ${error.message}`,
          category,
        },
      });
    }
  };

  /**
   * Track violation errors returned from the server.
   * These are server-side violations that occur during save/commit.
   * Event ID: TAI_ev02
   */
  const trackViolationErrors = (violations: Violation[]) => {
    for (const violation of violations) {
      pulse.trackEvent({
        type: 'Click',
        intent: 'Error',
        name: 'Violation error',
        object: {
          id: 'TAI_ev02',
          type: 'UIElement',
          elementType: 'Button',
          name: `Field: ${violation.field}, type: (${violation.type}), message: ${violation.message}`,
          category,
        },
      });
    }
  };

  /**
   * Convert React Hook Form field errors to a flat array of error info.
   * Handles nested errors by flattening them.
   */
  const convertFieldErrorsToErrorInfo = (fieldErrors: FieldErrors): ErrorInfo[] => {
    return Object.entries(fieldErrors).flatMap(([field, error]) => {
      if (error && typeof error === 'object' && !('type' in error)) {
        // Handle nested errors
        return Object.entries(error).map(([nestedField, nestedError]) => ({
          field: `${field}.${nestedField}`,
          type: (nestedError as { type: string })?.type || 'unknown',
          message: (nestedError as { message?: string })?.message,
        }));
      }
      return {
        field,
        type: (error as { type: string })?.type || 'unknown',
        message: (error as { message?: string })?.message,
      };
    });
  };

  return {
    trackValidationErrors,
    trackViolationErrors,
    convertFieldErrorsToErrorInfo,
  };
};
